/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import * as api from '@/api/tones';
import { TONES } from '@/store/types';
import { StoreDataCache, findCache, saveCache } from '@/store/cache';
import { TonesStart, UserTone } from '@/api/types/tones';
import { SecretPost } from '@/api/types/secret';
import { finalizeAction, StoreActionEnv, ActionType } from '@/store/actions';

export interface StoreUserTonesState {
  start: TonesStart;
  material: SecretPost;
  scene: string;
  nextPoint: StoreDataCache<number>[];
  limit: number;
  noMore: boolean;
  loading: boolean;
  adminList: UserTone[];
  id: string;
  tonesDetail: UserTone;
}

export default {
  namespaced: true,
  state: {
    start: null,
    material: null,
    scene: '',
    nextPoint: [],
    limit: 20,
    noMore: false,
    loading: false,
    adminList: [],
    id: '',
    tonesDetail: null,
  },
  actions: {
    [TONES.GET_TONES_START](
      { commit }: StoreActionEnv<StoreUserTonesState>,
    ) {
      return new Promise((resolve, reject) => {
        api.getTonesStart().then((res) => {
          commit(TONES.GET_TONES_START, {
            start: res.data,
          });
          resolve();
        }).catch(reject);
      });
    },
    [TONES.GET_TONES_ACQUIRE]() {
      return new Promise((resolve, reject) => {
        api.getTonesAcquire({ errcodeExpected: [200, 400] }).then((res) => {
          resolve(res);
        }).catch(reject);
      });
    },
    [TONES.GET_TONES_MATERIAL]({ commit }: StoreActionEnv<StoreUserTonesState>) {
      return new Promise((resolve, reject) => {
        api.getTonesMaterial().then((res) => {
          commit(TONES.GET_TONES_MATERIAL, {
            material: res.data,
          });
          resolve();
        }).catch(reject);
      });
    },
    [TONES.POST_USER_TONES](_, body) {
      return new Promise((resolve, reject) => {
        api.postUserTones(body).then((res) => {
          resolve(res);
        }).catch(reject);
      });
    },
    [TONES.GET_USER_TONES](
      { state, commit }: StoreActionEnv<StoreUserTonesState>,
      { action: rawAction, scene, force }: { action: ActionType; scene: string; force: boolean },
    ) {
      const cache = findCache(state.nextPoint, scene);
      const action = finalizeAction(rawAction, state.scene === scene, state.noMore && !force);
      if (action && !state.loading) {
        commit(TONES.GET_USER_TONES, { status: 'start' });
        const nextPoint = action === 'reload' ? null : cache;
        return new Promise((resolve, reject) => {
          api.getUserTones(scene, nextPoint, state.limit).then((res) => {
            commit(TONES.GET_USER_TONES, {
              status: 'success',
              scene,
              action,
              data: res.data,
              extra: res.extra,
            });
            resolve();
          }).catch(() => {
            commit(TONES.GET_USER_TONES, { status: 'failure' });
            reject();
          });
        });
      }
      return Promise.resolve();
    },
    [TONES.GET_USER_TONES_DETAIL](
      { state, commit }: StoreActionEnv<StoreUserTonesState>,
      { action: rawAction, id }: { action: ActionType; id: string },
    ) {
      const action = finalizeAction(rawAction, state.id === id);
      if (action) {
        return new Promise((resolve, reject) => {
          api.getUserTonesDetail(id).then((res) => {
            commit(TONES.GET_USER_TONES_DETAIL, {
              id,
              tonesDetail: res.data,
            });
            resolve();
          }).catch(() => {
            reject();
          });
        });
      }
      return Promise.resolve();
    },
    [TONES.PUT_USER_TONES_DETAIL](_, { id, main, secondary }: {
      id: string;
      main: string;
      secondary: string;
    }) {
      return new Promise((resolve, reject) => {
        api.putUserTonesDetail(id, main, secondary).then((res) => {
          resolve(res);
        }).catch(reject);
      });
    },
  },
  mutations: {
    [TONES.GET_TONES_START](state: StoreUserTonesState, { start }) {
      state.start = start;
    },
    [TONES.GET_TONES_MATERIAL](state: StoreUserTonesState, { material }) {
      state.material = material;
    },
    [TONES.GET_USER_TONES](state: StoreUserTonesState, { status, scene, action, data, extra }: {
      status: string;
      scene: string;
      action: string;
      data: UserTone[];
      extra: { nextPoint?: number };
    }) {
      if (status === 'start') {
        state.loading = true;
      } else {
        if (status === 'success') {
          state.scene = scene;
          if (action === 'reload') { // 刷新列表
            state.adminList = data;
          } else if (action === 'more') { // 获取更多
            state.adminList = state.adminList.concat(data);
          }
          state.noMore = (data && data.length) < state.limit;
          if (extra.nextPoint) {
            saveCache(state.nextPoint, scene, extra.nextPoint);
          }
        }
        state.loading = false;
      }
    },
    [TONES.GET_USER_TONES_DETAIL](state: StoreUserTonesState, { id, tonesDetail }) {
      state.id = id;
      state.tonesDetail = tonesDetail;
    },
  },
};
